import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { authFetch, useAuth, API } from "../context/AuthContext";
import { PaperPlaneTilt, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

// Convert https://... backend URL to wss://.../api/ws/chat/...
function wsUrl(matchId, token) {
    const base = API.replace(/^http/, "ws");
    return `${base}/ws/chat/${matchId}?token=${encodeURIComponent(token)}`;
}

export default function ChatRoom() {
    const { matchId } = useParams();
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [other, setOther] = useState(null);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const bottomRef = useRef(null);
    const wsRef = useRef(null);
    const reconnectTimer = useRef(null);

    // Initial load via REST (gives us other user info + history)
    const loadInitial = useCallback(async () => {
        try {
            const res = await authFetch("get", `/messages/${matchId}`);
            setMessages(res.data.messages);
            setOther(res.data.other_user);
        } catch {
            toast.error("Failed to load conversation");
        } finally {
            setLoading(false);
        }
    }, [matchId]);

    // Open WebSocket connection with auto-reconnect
    const openWebSocket = useCallback(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const ws = new WebSocket(wsUrl(matchId, token));
        wsRef.current = ws;

        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (evt) => {
            try {
                const data = JSON.parse(evt.data);
                if (data.type === "message" && data.message) {
                    setMessages((prev) => {
                        // Dedupe by message_id (skip our own optimistic message that WS echoes)
                        if (prev.some((m) => m.message_id === data.message.message_id)) return prev;
                        // Replace optimistic temp version if it matches
                        const filtered = prev.filter(
                            (m) => !(m.message_id?.startsWith("temp_") && m.sender_id === data.message.sender_id && m.text === data.message.text)
                        );
                        return [...filtered, data.message];
                    });
                }
            } catch { /* ignore */ }
        };
        ws.onclose = () => {
            setWsConnected(false);
            wsRef.current = null;
            // Auto-reconnect after 2s
            reconnectTimer.current = setTimeout(openWebSocket, 2000);
        };
        ws.onerror = () => ws.close();
    }, [matchId]);

    useEffect(() => {
        loadInitial();
        openWebSocket();
        return () => {
            if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.close();
            }
        };
    }, [loadInitial, openWebSocket]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault();
        const t = text.trim();
        if (!t || sending) return;
        setSending(true);
        const tempId = `temp_${Date.now()}`;
        const optimistic = {
            message_id: tempId, match_id: matchId, sender_id: user.user_id,
            text: t, created_at: new Date().toISOString(), read: false,
        };
        setMessages((m) => [...m, optimistic]);
        setText("");

        try {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ text: t }));
            } else {
                // Fallback to REST if WS not connected
                const res = await authFetch("post", `/messages/${matchId}`, { text: t });
                setMessages((m) => m.map((x) => (x.message_id === tempId ? res.data : x)));
            }
        } catch (err) {
            setMessages((m) => m.filter((x) => x.message_id !== tempId));
            toast.error(err.response?.data?.detail || "Failed to send");
            setText(t);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-12 px-4 sm:px-6">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                        <Link to="/messages" className="text-muted hover:text-gold transition-colors" data-testid="back-to-inbox">
                            <ArrowLeft size={20} />
                        </Link>
                        {other && (
                            <div className="flex-1 flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xl text-gold">
                                    {other.picture ? (
                                        <img src={other.picture} alt="" className="w-full h-full rounded-full object-cover" />
                                    ) : (
                                        <span className="font-serif">{SIGN_GLYPH[other.sun]}</span>
                                    )}
                                </div>
                                <div>
                                    <h2 className="font-serif text-xl leading-tight flex items-center gap-2">
                                        {other.name}
                                        {wsConnected && (
                                            <span className="w-2 h-2 rounded-full bg-emerald-400" title="Live" data-testid="ws-live" />
                                        )}
                                    </h2>
                                    <p className="text-xs text-muted">
                                        {SIGN_GLYPH[other.sun]} {other.sun} · ☽ {other.moon} · ↑ {other.rising}
                                    </p>
                                </div>
                            </div>
                        )}
                        {other && (
                            <Link
                                to={`/compatibility/${other.user_id}`}
                                className="text-gold hover:opacity-80 transition-opacity"
                                data-testid="chat-view-synastry"
                                aria-label="View synastry"
                            >
                                <Sparkle weight="duotone" size={22} />
                            </Link>
                        )}
                    </div>

                    {/* Messages */}
                    <div className="glass p-4 min-h-[60vh] max-h-[65vh] overflow-y-auto" data-testid="chat-window">
                        {loading && <p className="text-muted text-center py-8">Reading the threads...</p>}
                        {!loading && messages.length === 0 && (
                            <div className="text-center py-16">
                                <p className="font-serif text-2xl text-gold">✦</p>
                                <p className="font-serif text-xl mt-4">A new conversation</p>
                                <p className="text-muted text-sm mt-2">
                                    Send the first message. The stars are listening.
                                </p>
                            </div>
                        )}
                        <div className="space-y-3">
                            {messages.map((m) => {
                                const mine = m.sender_id === user.user_id;
                                return (
                                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                        <div
                                            className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                                                mine
                                                    ? "bg-gradient-to-br from-[#E6C387] to-[#F5D68F] text-[#03030A] rounded-br-sm"
                                                    : "bg-white/5 border border-white/10 rounded-bl-sm"
                                            }`}
                                            data-testid={mine ? "msg-mine" : "msg-theirs"}
                                        >
                                            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.text}</p>
                                            <p className={`text-[10px] mt-1 ${mine ? "text-[#03030A]/60" : "text-muted"}`}>
                                                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={bottomRef} />
                        </div>
                    </div>

                    {/* Composer */}
                    <form onSubmit={send} className="mt-3 flex gap-2 glass p-2 rounded-full" data-testid="chat-composer">
                        <input
                            type="text"
                            placeholder="Speak to the stars..."
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="flex-1 bg-transparent px-4 py-2 outline-none text-foreground placeholder:text-muted"
                            data-testid="chat-input"
                            maxLength={2000}
                        />
                        <button
                            type="submit"
                            disabled={!text.trim() || sending}
                            className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E6C387] to-[#F5D68F] flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                            data-testid="chat-send"
                            aria-label="Send"
                        >
                            <PaperPlaneTilt size={18} weight="fill" className="text-[#03030A]" />
                        </button>
                    </form>
                </div>
            </main>
        </>
    );
}
